/**
 * Hash routes:
 *   #/                         main landscape
 *   #/m/<module>               a module
 *   #/m/<module>/<tab>/<item>  a module with a tab (subsystem, process, object, ai, …) and an item selected
 *   #/layers[/<tab>/<item>]    the layered (ISA-95) architecture view, optionally with a layer / system selected
 *   #/biz/<module>[/<sub>]     the business explainer page of a module (no 3D), optionally scrolled to one subsystem
 *   #/learn[/<chapter>[/<sec>]] the manufacturing academy (no 3D): chapter list, one chapter, scrolled to one lesson
 *   #/path[/<role>]            the training paths (no 3D): levels and roles, or one role's path with progress
 *   #/glossary[/<term>]        the glossary (no 3D), optionally opened at one term
 */
export interface Route { view: "map" | "module" | "layers" | "biz" | "learn" | "glossary" | "path"; module?: string; tab?: string; item?: string }

export function parse(hash = location.hash): Route {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean).map(decodeURIComponent);
  if (parts[0] === "layers") return { view: "layers", ...(parts[1] ? { tab: parts[1] } : {}), ...(parts[2] ? { item: parts[2] } : {}) };
  if (parts[0] === "learn") return { view: "learn", ...(parts[1] ? { item: parts[1] } : {}), ...(parts[1] && parts[2] ? { tab: parts[2] } : {}) };
  if (parts[0] === "glossary" || parts[0] === "path") return { view: parts[0], ...(parts[1] ? { item: parts[1] } : {}) };
  if (parts[0] === "biz" && parts[1]) return { view: "biz", module: parts[1], ...(parts[2] ? { item: parts[2] } : {}) };
  if (parts[0] === "m" && parts[1]) return { view: "module", module: parts[1], ...(parts[2] ? { tab: parts[2] } : {}), ...(parts[3] ? { item: parts[3] } : {}) };
  return { view: "map" };
}

export function href(r: Route): string {
  if (r.view === "layers") return "#/layers" + [r.tab, r.item].filter(Boolean).map(s => "/" + encodeURIComponent(s!)).join("");
  if (r.view === "learn" || r.view === "glossary" || r.view === "path") return `#/${r.view}` + [r.item, r.item ? r.tab : undefined].filter(Boolean).map(s => "/" + encodeURIComponent(s!)).join("");
  if (r.view === "biz" && r.module) return "#/biz/" + [r.module, r.item].filter(Boolean).map(s => encodeURIComponent(s!)).join("/");
  if (r.view === "map" || !r.module) return "#/";
  return "#/m/" + [r.module, r.tab, r.item].filter(Boolean).map(s => encodeURIComponent(s!)).join("/");
}

export function go(r: Route, replace = false): void {
  const h = href(r);
  if (location.hash === h) return;
  if (replace) history.replaceState(null, "", h); else location.hash = h;
  if (replace) window.dispatchEvent(new HashChangeEvent("hashchange"));
}

/** Document pages: plain scrolling pages that run without the 3D app (main.ts). */
export const isDoc = (r: Route): boolean => r.view === "biz" || r.view === "learn" || r.view === "glossary" || r.view === "path";

export function onRoute(fn: (r: Route) => void): void {
  window.addEventListener("hashchange", () => fn(parse()));
}
